import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import api, { mesasService, productosService, promocionesService, cajaService, reservacionesService } from '../../services/api';
import { SOCKET_URL } from '../../utils/socket';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../components/ui/Toast';
import Dialogo from '../../components/ui/Dialogo';
import ModalLlaveAcceso from '../../components/ModalLlaveAcceso';
import { btnPrimario, btnSecundario, money, segmentos, segmento } from '../../components/ui/estilos';
import Cuentas from '../cuentas/Cuentas';
import PlanoSalon from './PlanoSalon';
import CuadriculaSalon from './CuadriculaSalon';
import PanelMesa, { Accion, DetalleMesa } from './PanelMesa';
import TomaPedido, { Destino } from './TomaPedido';
import BarraSalon from './BarraSalon';
import { useAcomodar } from './useAcomodar';
import { Area, ESTADOS, Estado, Grupo, Mesa, agrupar, conPosicion, minutosDesde, duracion, zonaDe } from './mesas';

const VISTA_KEY = 'chargoy.salon.vista';
const TICK_MS = 60_000;

export default function Salon() {
  const { usuario } = useAuth();
  const navigate = useNavigate();
  const rol = usuario?.rol || '';
  const esMesero = rol === 'mesero', esAdmin = rol === 'admin';
  const puedeCobrar = rol === 'admin' || rol === 'cajero';
  const puedeEditar = rol !== 'cajero';
  const [toast, say] = useToast();

  const [mesas, setMesas] = useState<Mesa[]>([]);
  const [areas, setAreas] = useState<Area[]>([]);
  const [productos, setProductos] = useState<any[]>([]);
  const [promociones, setPromociones] = useState<any[]>([]);
  const [llevar, setLlevar] = useState<any[]>([]);
  const [reservas, setReservas] = useState<any[]>([]);
  const [cargando, setCargando] = useState(true);
  const [, setTick] = useState(0);

  const [zona, setZona] = useState<string>('todas');
  const [filtro, setFiltro] = useState<Estado | null>(null);
  const [soloMias, setSoloMias] = useState(false);
  const [vista, setVista] = useState<'plano' | 'cuadricula'>(() => localStorage.getItem(VISTA_KEY) === 'cuadricula' ? 'cuadricula' : 'plano');
  const [selId, setSelId] = useState<string | null>(null);
  const [pestana, setPestana] = useState<'mesa' | 'llevar'>('mesa');
  const [personas, setPersonas] = useState(2);
  const [cuenta, setCuenta] = useState<any>(undefined);
  const [modo, setModo] = useState<null | 'unir' | 'mover'>(null);
  const [unirSel, setUnirSel] = useState<string[]>([]);
  const [pedido, setPedido] = useState<{ titulo: string; destino: Destino } | null>(null);
  const [cobro, setCobro] = useState<{ mesaId?: string; llevar?: any } | null>(null);
  const [llevarSel, setLlevarSel] = useState<any>(null);
  const [cambioEstado, setCambioEstado] = useState<{ paso: 'elegir' | 'llave'; estado?: Estado } | null>(null);

  const cargarMesas = useCallback(() => mesasService.getAll().then(r => setMesas(conPosicion(r.data))).catch(() => say('No se pudieron cargar las mesas.')), [say]);
  const cargarLlevar = useCallback(() => cajaService.getParaLlevar().then(r => setLlevar(Array.isArray(r.data) ? r.data : [])).catch(() => setLlevar([])), []);
  const cargarAreas = useCallback(() => api.get('/mesas/areas').then(r => setAreas(r.data)).catch(() => setAreas([])), []);

  useEffect(() => {
    const hoy = new Date().toLocaleDateString('en-CA');
    Promise.all([
      cargarMesas(), cargarAreas(), cargarLlevar(),
      productosService.getAll().then(r => setProductos(r.data.filter((p: any) => !p.suspendido))),
      promocionesService.getAll().then(r => {
        const dia = new Date().getDay();
        setPromociones(r.data.filter((p: any) => p.activa && p.items?.length && (!p.dias_semana || p.dias_semana.split(',').map(Number).includes(dia))));
      }),
      reservacionesService.getAll(hoy).then(r => setReservas(r.data.filter((x: any) => x.estado === 'confirmada'))).catch(() => {}),
    ]).catch(() => say('No se pudo cargar todo el salón. Revisa la conexión.')).finally(() => setCargando(false));
  }, [cargarMesas, cargarAreas, cargarLlevar, say]);

  // En vivo: otro mesero abrió una mesa, cocina marcó un platillo listo…
  useEffect(() => {
    const socket = io(SOCKET_URL, { auth: { token: localStorage.getItem('token') } });
    const recargar = () => { cargarMesas(); cargarLlevar(); };
    socket.on('mesa_actualizada', (p: any) => { recargar(); if (p?.plano) cargarAreas(); });
    socket.on('comanda_actualizada', recargar);
    const t = setInterval(() => setTick(n => n + 1), TICK_MS); // minutos en mesa
    return () => { socket.disconnect(); clearInterval(t); };
  }, [cargarMesas, cargarLlevar, cargarAreas]);

  const grupos = useMemo(() => agrupar(mesas), [mesas]);
  const zonas = useMemo(() => [...new Set(mesas.map(zonaDe))].sort(), [mesas]);
  const zonaActiva = zona !== 'todas' && zonas.includes(zona) ? zona : zonas.length === 1 ? zonas[0] : 'todas';
  const enZona = (g: Grupo) => zonaActiva === 'todas' || zonaDe(g.principal) === zonaActiva;
  const sel = grupos.find(g => g.principal.id === selId) || null;

  const acomodo = useAcomodar(mesas, areas, zonaActiva === 'todas' ? zonas[0] : zonaActiva, () => { cargarMesas(); cargarAreas(); say('Plano guardado.'); }, say);

  // Cuenta de la mesa seleccionada (items y comanda para agregar/pre-cuenta)
  useEffect(() => {
    setCuenta(undefined);
    if (!sel || !['ocupada', 'por_cobrar', 'unida'].includes(sel.estado)) return;
    let vivo = true;
    cajaService.getCuenta(sel.principal.id).then(r => vivo && setCuenta(r.data)).catch(() => vivo && setCuenta(null));
    return () => { vivo = false; };
  }, [selId, sel?.estado, sel?.total, sel?.listos]); // eslint-disable-line react-hooks/exhaustive-deps

  const coincide = (g: Grupo) => {
    if (modo) return true;
    if (filtro && g.estado !== filtro) return false;
    if (esMesero && soloMias && g.principal.mesero && g.principal.mesero !== usuario?.nombre) return false;
    return true;
  };
  const resaltar = (g: Grupo) => {
    const id = g.principal.id;
    if (modo === 'unir' && unirSel.includes(id)) return '3px solid var(--brand-texto)';
    if (modo === 'mover' && g.estado === 'libre') return '3px dashed var(--brand-texto)';
    if (!modo && selId === id) return '3px solid var(--brand-texto)';
    return 'none';
  };

  const tocar = (g: Grupo) => {
    const id = g.principal.id;
    if (modo === 'unir') {
      if (g.miembros.length > 1 || !['libre', 'ocupada'].includes(g.estado)) return say('Esa mesa no se puede unir.');
      // El backend une de dos en dos (mesa_unida_con).
      return setUnirSel(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id].slice(-2));
    }
    if (modo === 'mover') {
      if (g.estado !== 'libre') return say('Elige una mesa libre.');
      const origen = sel!;
      return api.patch(`/mesas/${origen.principal.id}/cambiar`, { mesa_destino_id: id })
        .then(() => { say(`Mesa ${origen.etiqueta} movida a la ${g.etiqueta}.`); setSelId(id); })
        .catch((e: any) => say(e.response?.data?.error || 'No se pudo cambiar la mesa.'))
        .finally(() => { setModo(null); cargarMesas(); });
    }
    setSelId(id);
    setPestana('mesa');
    setPersonas(Math.min(2, g.principal.capacidad));
  };

  const confirmarUnion = () => {
    if (unirSel.length < 2) return say('Elige 2 mesas.');
    const [a, b] = unirSel;
    mesasService.unirMesas(a, b)
      .then(() => { say(`Mesas ${unirSel.map(id => mesas.find(m => m.id === id)?.numero).join(' + ')} unidas.`); setSelId(a); })
      .catch(() => say('No se pudieron unir las mesas.'))
      .finally(() => { setModo(null); setUnirSel([]); cargarMesas(); });
  };

  const estado = (id: string, e: Estado, p?: number) => mesasService.updateEstado(id, e, p).then(cargarMesas);

  const imprimirPrecuenta = async () => {
    if (!cuenta?.comanda_id) throw new Error('sin comanda');
    await api.post('/caja/imprimir-precuenta', { comanda_id: cuenta.comanda_id });
  };

  const abrirPedido = (g: Grupo, comandaId: string | null) =>
    setPedido({ titulo: g.miembros.length > 1 ? `Mesas ${g.etiqueta}` : `Mesa ${g.etiqueta}`, destino: { tipo: 'mesa', mesaId: g.principal.id, comandaId } });

  const detalle: DetalleMesa | null = sel ? (() => {
    const m = sel.principal, e = sel.estado, min = minutosDesde(m.ocupada_desde);
    const lugares = sel.miembros.reduce((s, x) => s + x.capacidad, 0);
    const reserva = reservas.find(r => r.mesa_id === m.id);
    const info: { k: string; v: string }[] = [];
    if (m.mesero) info.push({ k: 'Mesero', v: m.mesero });
    if (['ocupada', 'por_cobrar', 'unida'].includes(e)) { info.push({ k: 'Personas', v: `${m.personas_actual || 0} de ${lugares}` }); info.push({ k: 'Tiempo', v: `${duracion(min)}` }); }
    if (e === 'libre') info.push({ k: 'Lugares', v: String(lugares) });
    if (e === 'reservada' && reserva) {
      info.push({ k: 'Reserva', v: reserva.cliente_nombre }, { k: 'Hora', v: `${String(reserva.hora).slice(0, 5)} h` }, { k: 'Personas', v: String(reserva.personas) });
      if (reserva.telefono) info.push({ k: 'Teléfono', v: reserva.telefono });
    }
    let nota = '';
    if (e === 'reservada' && reserva?.notas) nota = reserva.notas;
    if (e === 'por_cobrar' && esMesero) nota = 'Cuenta pedida. La cobra caja; aún puedes agregar platillos.';
    if (e === 'limpieza') nota = 'Márcala como libre cuando esté limpia y montada.';
    if (e === 'ocupada' && rol === 'cajero') nota = 'La mesa sigue abierta. Pasa a “Por cobrar” cuando el mesero pida la cuenta.';

    const P = (label: string, onClick: () => void): Accion => ({ label, onClick, primaria: true });
    const S = (label: string, onClick: () => void): Accion => ({ label, onClick });
    const comanda = cuenta?.comanda_id ?? null;
    const total = cuenta?.total ?? sel.total;
    const agregar = () => abrirPedido(sel, comanda);
    const cobrar = () => setCobro({ mesaId: m.id });
    const reimp = () => imprimirPrecuenta().then(() => say('Pre-cuenta reimpresa.')).catch(() => say('No se pudo imprimir la pre-cuenta.'));
    let acciones: Accion[] = [];
    if (e === 'libre' && puedeEditar) acciones = [
      P('Abrir mesa', () => estado(m.id, 'ocupada', personas).then(() => { say(`Mesa ${sel.etiqueta} abierta para ${personas}.`); abrirPedido(sel, null); })),
      S('Unir con otra mesa', () => { setModo('unir'); setUnirSel([m.id]); }),
    ];
    if (e === 'ocupada' || e === 'unida') acciones = [
      ...(puedeEditar ? [P('Agregar platillos', agregar), S('Pedir cuenta', () =>
        estado(m.id, 'por_cobrar', m.personas_actual)
          .then(() => imprimirPrecuenta().then(() => say('Pre-cuenta impresa. La mesa pasa a caja.'), () => say('La mesa pasa a caja (no se pudo imprimir la pre-cuenta).'))))] : []),
      ...(puedeCobrar && comanda ? [S(`Cobrar ${money(total)}`, cobrar)] : []),
      ...(puedeEditar && e === 'ocupada' ? [S('Cambiar de mesa', () => setModo('mover'))] : []),
    ];
    if (e === 'por_cobrar') acciones = [
      ...(puedeCobrar ? [P(`Cobrar ${money(total)}`, cobrar)] : []),
      ...(puedeEditar ? [S('Agregar platillos', agregar)] : []),
      S('Reimprimir pre-cuenta', reimp),
    ];
    if (e === 'reservada') acciones = [
      // Sentar cierra la reservación como "completada" (cuenta como reserva, no walk-in).
      ...(puedeEditar ? [P('Sentar a los invitados', () => estado(m.id, 'ocupada', reserva?.personas || personas).then(() => {
        say(`${reserva?.cliente_nombre || 'Invitados'} sentados en la mesa ${sel.etiqueta}.`);
        if (reserva) reservacionesService.cerrar(reserva.id, 'completada').then(() => setReservas(rs => rs.filter(x => x.id !== reserva.id))).catch(() => {});
      }))] : []),
      S('Ver en Agenda', () => navigate('/agenda')),
    ];
    if (e === 'limpieza') acciones = [P('Marcar como libre', () => estado(m.id, 'libre').then(() => say(`Mesa ${sel.etiqueta} lista.`)))];

    const enlaces: Accion[] = [];
    if (sel.miembros.length > 1 && puedeEditar) enlaces.push(S('Separar mesas', () => mesasService.unirMesas(m.id, null).then(() => { say('Mesas separadas.'); cargarMesas(); })));
    if (esAdmin) enlaces.push(S('Cambiar estado…', () => setCambioEstado({ paso: 'elegir' })));

    return {
      titulo: sel.miembros.length > 1 ? `Mesas ${sel.etiqueta}` : `Mesa ${sel.etiqueta}`,
      estado: e, zona: zonaDe(m), info, nota,
      items: ['ocupada', 'por_cobrar', 'unida'].includes(e) ? (cuenta === undefined ? null : cuenta?.items || []) : [],
      total,
      pedirPersonas: e === 'libre' && puedeEditar, acciones, enlaces,
    };
  })() : null;

  if (cargando) return <div style={{ display: 'grid', placeItems: 'center', height: 256, color: 'var(--muted)', fontSize: 17 }}>Cargando salón…</div>;

  if (cobro) return (
    <div style={{ height: 'calc(var(--vh100, 100vh) - 124px)' }}>
      <Cuentas embebido mesaInicial={cobro.mesaId} llevarInicial={cobro.llevar}
        onCerrar={() => { setCobro(null); setSelId(null); cargarMesas(); cargarLlevar(); }} />
    </div>
  );

  const visibles = grupos.filter(enZona);
  const verPlano = vista === 'plano' && zonaActiva !== 'todas';

  return (
    <div style={{ height: 'calc(var(--vh100, 100vh) - 124px)', minHeight: 520, display: 'flex', flexDirection: 'column', gap: 14 }}>
      <BarraSalon
        zonas={zonas} zona={zonaActiva} onZona={z => { setZona(z); setFiltro(null); }} grupos={grupos}
        visibles={visibles} filtro={filtro} onFiltro={setFiltro}
        esMesero={esMesero} soloMias={soloMias} onSoloMias={() => setSoloMias(v => !v)}
        vista={vista} onVista={v => { setVista(v); localStorage.setItem(VISTA_KEY, v); }} mostrarVistas={zonaActiva !== 'todas'}
        puedeEditar={puedeEditar && !acomodo.activo} onUnir={() => { setModo('unir'); setUnirSel([]); }}
        esAdmin={esAdmin && verPlano && !acomodo.activo} onAcomodar={acomodo.iniciar}
      />

      {modo && (
        <div role="status" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 10px 10px 18px', borderRadius: 12, background: 'var(--strong)', color: 'var(--on-strong)' }}>
          <div style={{ flex: 1, fontSize: 17, fontWeight: 700 }}>
            {modo === 'unir'
              ? (unirSel.length ? `Uniendo: ${unirSel.map(id => mesas.find(m => m.id === id)?.numero).join(' + ')}. ${unirSel.length < 2 ? 'Toca la otra mesa.' : 'Confirma la unión.'}` : 'Toca las 2 mesas que quieres unir.')
              : `Toca la mesa libre a donde se mueve la ${sel?.etiqueta}.`}
          </div>
          <button onClick={() => { setModo(null); setUnirSel([]); }} style={{ ...btnSecundario, background: 'transparent', color: 'var(--on-strong)', borderColor: 'var(--on-strong)' }}>Cancelar</button>
          {modo === 'unir' && <button onClick={confirmarUnion} disabled={unirSel.length < 2} style={{ ...btnPrimario, height: 48, fontSize: 16, opacity: unirSel.length < 2 ? .5 : 1 }}>Unir {unirSel.length === 2 ? '2 mesas' : ''}</button>}
        </div>
      )}

      {acomodo.activo && acomodo.barra}

      <div style={{ flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: acomodo.activo ? 'minmax(0,1fr)' : 'minmax(0,1fr) clamp(300px, 32vw, 380px)', gap: 16 }}>
        <section aria-label="Mesas" style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 16, padding: 'clamp(10px, 2vw, 22px)', overflow: 'auto', minHeight: 0, containerType: 'size' }}>
          {!mesas.length && <p style={{ color: 'var(--muted)', fontSize: 17 }}>No hay mesas. Créalas en Ajustes › Negocio.</p>}
          {acomodo.activo ? (
            <PlanoSalon grupos={[]} areas={acomodo.areas} acomodando mesasEdicion={acomodo.mesas}
              seleccion={acomodo.seleccion} onSeleccionar={acomodo.seleccionar}
              onCambiarMesa={acomodo.cambiarMesa} onCambiarArea={acomodo.cambiarArea} />
          ) : verPlano ? (
            <PlanoSalon grupos={visibles} areas={areas.filter(a => (a.zona || zonas[0]) === zonaActiva)}
              selId={selId} resaltar={resaltar} atenuar={g => !coincide(g)} onTocar={tocar} />
          ) : (
            <CuadriculaSalon selId={selId} resaltar={resaltar} atenuar={g => !coincide(g)} onTocar={tocar}
              secciones={zonaActiva === 'todas' && zonas.length > 1
                ? zonas.map(z => {
                    const gs = grupos.filter(g => zonaDe(g.principal) === z);
                    const libres = gs.filter(g => g.estado === 'libre').length;
                    const servicio = gs.filter(g => ['ocupada', 'por_cobrar', 'unida'].includes(g.estado)).length;
                    return { titulo: z, resumen: `${libres} libres · ${servicio} en servicio`, grupos: gs };
                  })
                : [{ titulo: '', resumen: '', grupos: visibles }]} />
          )}
        </section>

        {!acomodo.activo && (
          <PanelMesa pestana={pestana} onPestana={setPestana} detalle={detalle} personas={personas} onPersonas={setPersonas}
            llevar={llevar} onLlevar={setLlevarSel}
            onNuevoLlevar={() => setPedido({ titulo: 'Para llevar', destino: { tipo: 'llevar', comandaId: null } })} />
        )}
      </div>

      {pedido && (
        <TomaPedido titulo={pedido.titulo} destino={pedido.destino} productos={productos} promociones={promociones}
          onCerrar={() => setPedido(null)} onEnviado={m => { setPedido(null); say(m); cargarMesas(); cargarLlevar(); }} />
      )}

      {llevarSel && (
        <Dialogo titulo={llevarSel.cliente_nombre || 'Para llevar'} onCerrar={() => setLlevarSel(null)}>
          <div style={{ fontSize: 17, color: 'var(--muted)' }}>{llevarSel.mesero} · {money(parseFloat(llevarSel.total))}</div>
          <button style={{ ...btnSecundario, height: 56, fontSize: 18 }} onClick={() => {
            setPedido({ titulo: llevarSel.cliente_nombre || 'Para llevar', destino: { tipo: 'llevar', comandaId: llevarSel.comanda_id, cliente: llevarSel.cliente_nombre } });
            setLlevarSel(null);
          }}>Agregar platillos</button>
          {puedeCobrar && <button style={btnPrimario} onClick={() => { setCobro({ llevar: llevarSel }); setLlevarSel(null); }}>Cobrar {money(parseFloat(llevarSel.total))}</button>}
        </Dialogo>
      )}

      {cambioEstado?.paso === 'elegir' && sel && (
        <Dialogo titulo={`Cambiar estado · Mesa ${sel.etiqueta}`} onCerrar={() => setCambioEstado(null)}>
          <p style={{ margin: 0, color: 'var(--muted)' }}>Solo para corregir una mesa que quedó en el estado equivocado.</p>
          {(['libre', 'ocupada', 'por_cobrar', 'reservada', 'limpieza'] as Estado[]).filter(e => e !== sel.estado).map(e => (
            <button key={e} style={{ ...btnSecundario, height: 52 }} onClick={() => setCambioEstado({ paso: 'llave', estado: e })}>{ESTADOS[e].label}</button>
          ))}
        </Dialogo>
      )}
      {cambioEstado?.paso === 'llave' && sel && (
        <ModalLlaveAcceso permiso="puede_modificar_comanda" titulo="Cambiar estado de mesa" descripcion={`Nuevo estado: ${ESTADOS[cambioEstado.estado!].label}`}
          onCancel={() => setCambioEstado(null)}
          onSuccess={() => { const e = cambioEstado.estado!; setCambioEstado(null); estado(sel.principal.id, e, sel.principal.personas_actual).then(() => say('Estado actualizado.')); }} />
      )}
      {toast}
    </div>
  );
}
